import {useCallback, useEffect} from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import apiFetch from '@wordpress/api-fetch'
import {useBrowerStorage} from '../useBrowserStorage'
import {useFavoriteCompetitions} from './useFavoriteCompetitions'
import {useLastViewedCompetitions} from './useLastViewedCompetitions'
import {COMPETITIONS_BOTTOM_BAR_CONFIG} from '../../config/competitionsBottomBarConfig'
import {CachedUserCompetition, CachedUserPoule, isCachedUserPoule} from '../../competitions/types'
import {xorDecipherAndParse} from '../../shared/utils'

dayjs.extend(utc)

export const EMPTY_ARRAY = []
export const USER_COMPETITIONS_VALIDATION = (x: any) => Array.isArray(x) // not very strict
export const USER_COMPETITIONS_EXPIRATION_VALIDATION = (x: any) =>
    typeof x === 'string' && dayjs(x).isValid()

export const useUserCompetitions = (): {
    cachedUserCompetitions: (CachedUserCompetition | CachedUserPoule)[]
} => {
    const {value: cachedUserCompetitions, setValue: setCachedUserCompetitions} = useBrowerStorage<
        (CachedUserCompetition | CachedUserPoule)[]
    >({
        key: COMPETITIONS_BOTTOM_BAR_CONFIG.USER_COMPS.LOCAL_STORAGE_KEY,
        defaultValue: EMPTY_ARRAY,
        getItemValidation: USER_COMPETITIONS_VALIDATION,
    })

    const {value: userCompetitionsExpiration, setValue: setUserCompetitionsExpiration} =
        useBrowerStorage<string>({
            key: COMPETITIONS_BOTTOM_BAR_CONFIG.USER_COMPS.EXPIRATION.LOCAL_STORAGE_KEY,
            defaultValue: '',
            getItemValidation: USER_COMPETITIONS_EXPIRATION_VALIDATION,
        })

    const {favoritesPoules} = useFavoriteCompetitions()
    const {lastVieweds} = useLastViewedCompetitions()

    const userCompetitionsIds = lastVieweds.filter(
        (x, index, arr) => arr.indexOf(x) === index,
    ) // remove the duplicates

    const joinedUserCompetitionsIds = userCompetitionsIds.sort((a, b) => a - b).join(',')
    const joinedPoulesCompetitionsIds = favoritesPoules.map(e => e.extPouleId).sort((a, b) => a - b).join(',')

    const fetchUserCompetitions = useCallback(async () => {
        const isFresh =
            userCompetitionsExpiration && dayjs.utc(userCompetitionsExpiration).isAfter(dayjs.utc())

        const badCompetitionCacheLength = userCompetitionsIds.length > 0 && cachedUserCompetitions.filter(e => !isCachedUserPoule(e)).length === 0
        const badPouleCacheLength = favoritesPoules.length > 0 && cachedUserCompetitions.filter(e => isCachedUserPoule(e)).length === 0


        if (!isFresh || badCompetitionCacheLength || badPouleCacheLength) {
            try {
                const data = await apiFetch<string>({
                    path: `competitions/v1/competitions/?competition_ids=${joinedUserCompetitionsIds}&poule_ids=${joinedPoulesCompetitionsIds}`,
                })
                const jsonData = xorDecipherAndParse(data)
                setCachedUserCompetitions(jsonData)
                setUserCompetitionsExpiration(
                    COMPETITIONS_BOTTOM_BAR_CONFIG.USER_COMPS.EXPIRATION.getNewExpiration(),
                )
            } catch (e) {
                console.error(e)
            }
        }
    }, [
        userCompetitionsExpiration,
        userCompetitionsIds.length,
        favoritesPoules.length,
        cachedUserCompetitions,
        joinedUserCompetitionsIds,
        joinedPoulesCompetitionsIds,
        setCachedUserCompetitions,
        setUserCompetitionsExpiration,
    ])

    useEffect(() => {
        fetchUserCompetitions()
    }, [fetchUserCompetitions])

    return {cachedUserCompetitions}
}
